import React from 'react';
import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ButtonClickEvent } from 'ui/base/types';
import { SearchInput } from 'ui/standard/input/search_input/search_input';

import {
  CountryCode,
  getLocalNameByCode,
} from 'services/base/countries';

import {
  RemovableLocationEntry,
  SearchLocationEntry,
  SearchLocationEntryProps,
} from 'ui/history/location_entry/location_entry';

import baseStyles from './location_input.module.css';
import { LocationInputStore } from './location_input_store';

type LocationInputStyles = {
  inputButton: string;
  plusIcon: string;
  placeholder: string;
};

const themedStyles: ThemedStyles<LocationInputStyles> = {
  [Theme.DAYLIGHT]: {
    inputButton: baseStyles.daylightInputButton,
    plusIcon: baseStyles.daylightPlusIcon,
    placeholder: baseStyles.daylightPlaceholder,
  },
  [Theme.MIDNIGHT]: {
    inputButton: baseStyles.midnightInputButton,
    plusIcon: baseStyles.midnightPlusIcon,
    placeholder: baseStyles.midnightPlaceholder,
  },
};

type LocationEntryDisplayProps = {
  store: LocationInputStore;
  code: CountryCode;
};

const LocationEntryDisplay = observer(({ store, code }: LocationEntryDisplayProps) => {
  const onRemoveClick = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.removeEntry(code);
  }), [store, code]);

  return <RemovableLocationEntry code={code} onRemoveClick={onRemoveClick}/>;
});

type LocationInputDisplayProps = {
  store: LocationInputStore;
  placeholder: string;
};

const LocationInputDisplay = observer(({ store, placeholder }: LocationInputDisplayProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.search.search.setValue('');
    store.search.modal.showModal();
  }), [store]);
  return (
    <>
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <div className={styles.placeholder}>
          {placeholder}
        </div>
        <div className={styles.plusIcon}/>
      </button>
      {store.entries.map((code) =>
        <LocationEntryDisplay store={store} code={code} key={code}/>)}
    </>
  );
});

type LocationInputProps = {
  placeholder: string;
  store: LocationInputStore;
};

@observer export class LocationInput extends React.Component<LocationInputProps> {
  @computed get currentEntryCodes(): Map<string, true> {
    const map: Map<string, true> = new Map();
    const ids = this.props.store.entries;
    ids.forEach((id) => map.set(id, true));
    return map;
  }

  filterEntry = (code: CountryCode, text: string): boolean => {
    if (this.currentEntryCodes.has(code)) {
      return false;
    }
    const trimmed = text.trim().toLocaleLowerCase();
    if (trimmed) {
      const label = getLocalNameByCode(code)?.toLowerCase();
      return label !== undefined && label.indexOf(trimmed) >= 0;
    }
    return true;
  };

  DisplayComponent = () => {
    return <LocationInputDisplay store={this.props.store} placeholder={this.props.placeholder} />;
  };

  ItemComponent = ({ data: code, ...restProps }: Omit<SearchLocationEntryProps, 'onSelect'>) => {
    const onSelect = React.useCallback(action(() => {
      this.props.store.commitEntry(code);
      this.props.store.search.modal.hideModal();
    }), [this.props.store, code]);
    return <SearchLocationEntry data={code} onSelect={onSelect} {...restProps}/>;
  };

  render() {
    return (
      <>
        <SearchInput
          placeholder={this.props.placeholder}
          store={this.props.store.search}
          data={this.props.store.options}
          filterFunc={this.filterEntry}
          keyFunc={(k) => k}
          ItemComponent={this.ItemComponent}
          DisplayComponent={this.DisplayComponent}
          selectOnly={false}
        />
      </>
    );
  }
}
